import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { Plus, Copy, Link2, Loader2, ExternalLink, SlidersHorizontal, QrCode, Printer } from "lucide-react";
import { toast } from "sonner";

const ORIGIN = window.location.origin;

export default function PaymentLinks() {
  const { user } = useAuth();
  const isMerchant = user.role === "merchant";
  const [links, setLinks] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState("");
  const [amountInApp, setAmountInApp] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [editPresets, setEditPresets] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const parsePresets = (s) => s.split(",").map((x) => parseFloat(x.trim())).filter((x) => x > 0);

  const load = useCallback(() => {
    api.get("/payment-links").then((r) => { setLinks(r.data.links); setMerchants(r.data.merchants || []); }).catch(() => setLinks([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!isMerchant && !merchantId) { toast.error("Select a merchant"); return; }
    setSaving(true);
    try {
      await api.post("/payment-links", { note: note || "Payment", amount_in_app: amountInApp, presets: amountInApp ? [] : parsePresets(presets), ...(isMerchant ? {} : { merchant_id: merchantId }) });
      toast.success("Reusable payment link created");
      setNote(""); setMerchantId(""); setPresets(""); setAmountInApp(false); setOpen(false);
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (l) => {
    try {
      await api.patch(`/payment-links/${l.id}`, { active: !l.active });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copy = (l) => { navigator.clipboard.writeText(`${ORIGIN}/pl/${l.link_id}`); toast.success("Link copied"); };

  const openEdit = (l) => { setEditLink(l); setEditPresets((l.presets || []).join(", ")); };
  const [qrLink, setQrLink] = useState(null);
  const printQr = () => {
    const el = document.getElementById("pl-qr-poster");
    if (!el) return;
    const w = window.open("", "_blank", "width=480,height=640");
    if (!w) { toast.error("Allow pop-ups to print the QR poster"); return; }
    w.document.title = "Payment QR";
    w.document.body.style.cssText = "font-family:system-ui;text-align:center;padding:32px";
    w.document.body.appendChild(w.document.importNode(el, true));
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const savePresets = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/payment-links/${editLink.id}`, { presets: parsePresets(editPresets) });
      toast.success("Quick amounts updated");
      setEditLink(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Payment links</h2>
          <p className="text-sm text-muted-foreground">Reusable one-tap links — share once, every customer enters their own amount.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-payment-link-button"><Plus className="mr-1.5 h-4 w-4" /> New link</Button>
          </DialogTrigger>
          <DialogContent data-testid="new-payment-link-dialog">
            <DialogHeader>
              <DialogTitle>Create a reusable payment link</DialogTitle>
              <DialogDescription>Customers open the link, type any amount, and pay. One link works for unlimited customers.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              {!isMerchant && (
                <div className="space-y-1.5">
                  <Label>Merchant</Label>
                  <Select value={merchantId} onValueChange={setMerchantId}>
                    <SelectTrigger data-testid="pl-merchant-select"><SelectValue placeholder="Select a merchant" /></SelectTrigger>
                    <SelectContent>
                      {merchants.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="pl-note">Label / description</Label>
                <Input id="pl-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Store counter, Online orders" data-testid="pl-note-input" />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-lg border bg-muted/40 p-3">
                <div className="min-w-0">
                  <Label>Customer enters amount in their UPI app</Label>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    ON — the link opens the UPI app with a <b>blank amount</b> (no amount asked on our page).
                    OFF — the customer types the amount on our page first.
                  </p>
                </div>
                <Switch checked={amountInApp} onCheckedChange={setAmountInApp} data-testid="pl-amount-in-app-switch" />
              </div>
              {!amountInApp && (
                <div className="space-y-1.5">
                  <Label htmlFor="pl-presets">Quick-amount buttons (optional)</Label>
                  <Input id="pl-presets" value={presets} onChange={(e) => setPresets(e.target.value)} placeholder="e.g. 199, 499, 999" data-testid="pl-presets-input" />
                  <p className="text-xs text-muted-foreground">Comma-separated. Leave blank to use the default ₹100 / ₹500 / ₹1000 / ₹2000.</p>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="pl-submit-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create link"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Link</TableHead>
                {!isMerchant && <TableHead>Merchant</TableHead>}
                <TableHead className="text-right">Orders</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links === null && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {links && links.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No payment links yet. Create one to share.</TableCell></TableRow>}
              {links && links.map((l) => (
                <TableRow key={l.id} data-testid={`payment-link-row-${l.link_id}`}>
                  <TableCell>
                    <div className="font-medium">{l.note}</div>
                    <div className="font-mono text-xs text-muted-foreground">/pl/{l.link_id}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {l.amount_in_app ? (
                        <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium">Amount entered in UPI app</span>
                      ) : (l.presets && l.presets.length ? l.presets : [100, 500, 1000, 2000]).map((p) => (
                        <span key={p} className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-mono text-muted-foreground">₹{p.toLocaleString("en-IN")}</span>
                      ))}
                    </div>
                  </TableCell>
                  {!isMerchant && <TableCell className="text-sm">{l.merchant_name || "—"}</TableCell>}
                  <TableCell className="text-right font-mono">{l.orders_count || 0}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={l.active} onCheckedChange={() => toggle(l)} data-testid={`pl-toggle-${l.link_id}`} />
                      <Badge className={l.active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}>{l.active ? "Active" : "Paused"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{dt(l.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setQrLink(l)} data-testid={`pl-qr-${l.link_id}`} title="QR poster"><QrCode className="h-4 w-4" /></Button>
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(l)} data-testid={`pl-edit-${l.link_id}`} title="Edit quick amounts"><SlidersHorizontal className="h-4 w-4" /></Button>
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => copy(l)} data-testid={`pl-copy-${l.link_id}`}><Copy className="h-4 w-4" /></Button>
                      <a href={`${ORIGIN}/pl/${l.link_id}`} target="_blank" rel="noreferrer">
                        <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`pl-open-${l.link_id}`}><ExternalLink className="h-4 w-4" /></Button>
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link2 className="h-3.5 w-3.5" /> Each payment spawns a fresh order for the exact amount — no extra paise is added.
      </div>

      <Dialog open={!!editLink} onOpenChange={(o) => !o && setEditLink(null)}>
        <DialogContent data-testid="pl-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit quick-amount buttons</DialogTitle>
            <DialogDescription>Comma-separated amounts shown to the customer. Leave blank to use the default ₹100 / ₹500 / ₹1000 / ₹2000.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pl-edit-presets">Quick amounts</Label>
            <Input id="pl-edit-presets" value={editPresets} onChange={(e) => setEditPresets(e.target.value)} placeholder="e.g. 199, 499, 999" data-testid="pl-edit-presets-input" />
          </div>
          <DialogFooter>
            <Button onClick={savePresets} disabled={savingEdit} data-testid="pl-edit-save">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save amounts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrLink} onOpenChange={(o) => !o && setQrLink(null)}>
        <DialogContent data-testid="pl-qr-dialog">
          <DialogHeader>
            <DialogTitle>Payment QR poster</DialogTitle>
            <DialogDescription>Print this and keep it at your counter. Customers scan it, enter any amount, and pay.</DialogDescription>
          </DialogHeader>
          {qrLink && (
            <div id="pl-qr-poster" className="flex flex-col items-center gap-3 py-2 text-center">
              <h2 style={{ fontWeight: 700, fontSize: 20, margin: 0 }}>{qrLink.merchant_name}</h2>
              <p style={{ margin: 0, color: "#666" }}>{qrLink.note}</p>
              <div className="rounded-xl bg-white p-3 border">
                <QRCodeSVG value={`${ORIGIN}/pl/${qrLink.link_id}`} size={220} />
              </div>
              <p style={{ margin: 0, fontFamily: "monospace", fontSize: 12 }}>{ORIGIN}/pl/{qrLink.link_id}</p>
              <p style={{ margin: 0, fontSize: 12, color: "#888" }}>Scan • Enter amount • Pay via any UPI app</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={printQr} data-testid="pl-qr-print"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
